import type { ReportingOverview, SameRepositoryScanDelta } from "../../api/types.js";
import { SUPPORTED_SCOPE_SUMMARY } from "../../lib/supported-scope.js";

export type ReportStampTone = "warn" | "ok";

export interface ReportStamp {
  line1: string;
  line2: string;
  tone: ReportStampTone;
}

export const REPORT_LENSES = ["executive", "reviewer", "technical"] as const;
export type ReportLens = (typeof REPORT_LENSES)[number];

export interface ReportLensCopy {
  heading: string;
  narrative: string;
  catalogCaption: string;
  compareCaption: string;
  emptyTitle: string;
  emptyDescription: string;
}

const HOSTED_POC = /hosted[-\s]?poc/iu;
const PUBLIC_BENCHMARK = /public[-\s]?benchmark|intentionally vulnerable/iu;
const SYNTHETIC = /synthetic/iu;

function haystack(limitations: readonly string[], scopeLabel: string): string {
  return [scopeLabel, ...limitations].join("\n");
}

function claimsLine(limitations: readonly string[]): string {
  if (limitations.length === 0) return SUPPORTED_SCOPE_SUMMARY;
  return limitations.join(" ");
}

/**
 * Honesty stamp from catalog limitations and scope. Hosted PoC, synthetic, and
 * public-benchmark evidence always warn and are never customer findings.
 * Does not invent SOC2/IEC marks.
 */
export function reportStamp(limitations: readonly string[], scopeLabel: string): ReportStamp {
  const text = haystack(limitations, scopeLabel);
  const line2 = claimsLine(limitations);

  if (HOSTED_POC.test(text) || HOSTED_POC.test(line2)) {
    return { line1: "Hosted PoC — not customer findings", line2, tone: "warn" };
  }
  if (PUBLIC_BENCHMARK.test(text) || PUBLIC_BENCHMARK.test(line2)) {
    return { line1: "Public benchmark — not customer findings", line2, tone: "warn" };
  }
  if (SYNTHETIC.test(text) || SYNTHETIC.test(line2)) {
    return { line1: "Synthetic source — not customer findings", line2, tone: "warn" };
  }

  return { line1: "Catalog-limited report", line2, tone: "ok" };
}

/** Historical compare is enabled only with ≥2 trend points or an available scan delta. */
export function compareReady(
  trend: ReportingOverview["trend"],
  scanDelta: SameRepositoryScanDelta,
): boolean {
  return (trend.available && trend.points.length >= 2) || scanDelta.available;
}

export function compareDisabledReason(
  trend: ReportingOverview["trend"],
  scanDelta: SameRepositoryScanDelta,
): string | null {
  if (compareReady(trend, scanDelta)) return null;
  if (!trend.available) {
    return trend.limitation || "Historical compare needs a comparable recorded trend.";
  }
  if (trend.points.length < 2) {
    return "Historical compare needs at least two recorded scan points, or an available scan delta.";
  }
  return scanDelta.limitation;
}

export function reportLensCopy(lens: ReportLens): ReportLensCopy {
  switch (lens) {
    case "executive":
      return {
        heading: "Assurance briefing",
        narrative:
          "These catalog items are the only reports this console can issue. Changing the lens does not invent totals or compliance marks.",
        catalogCaption: "What leadership can download from the catalog",
        compareCaption: "Scan-to-scan movement, only when evidence exists",
        emptyTitle: "No reports in the catalog",
        emptyDescription:
          "The reporting catalog is empty. That is not a clean bill of health and not a customer finding.",
      };
    case "reviewer":
      return {
        heading: "Review packet",
        narrative:
          "Read each catalog item's limitations before treating a download as a decision record. The item list is the same in every lens.",
        catalogCaption: "Catalog items for reviewer packets",
        compareCaption: "Compare only when two recorded points or a scan delta exist",
        emptyTitle: "No reports in the catalog",
        emptyDescription:
          "There is nothing to review in the catalog. An empty list is not evidence that the estate is clean.",
      };
    case "technical":
      return {
        heading: "Report descriptors",
        narrative:
          "Each row is an API catalog descriptor: title, period, limitations, and a same-origin download when the href is safe.",
        catalogCaption: "Technical catalog rows",
        compareCaption: "Trend points (max 4) and same-repository delta cells",
        emptyTitle: "No reports in the catalog",
        emptyDescription:
          "GET /reporting/reports returned no descriptors. The console will not fabricate a report.",
      };
  }
}
